import React, { Suspense, useEffect, useState } from 'react'
import { Container, Box, CircularProgress } from '@mui/material';
import { useOutletContext, useNavigate, Outlet, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getSelectedAccount, setSelectedAccount } from '../../../redux/selectedAccountSlice';
import { getSelectedBuilding } from '../../../redux/selectedBuildingSlice';
import { getSelectedSubscription, setSelectedSubscription } from '../../../redux/selectedSubscriptionSlice';
import { usePutPauseStateCodeMutation, usePutResumeStateCodeMutation, usePutActivateStateCodeMutation, usePutResetInitializationStateCodeMutation} from '../../../api/subscription-manager/stateCodeApiSlice'
import { usePostResetPredictiveHeatControlMutation } from '../../../api/subscription-manager/predictiveHeatControlSlice';
import { useGetJobSpecificationsQuery } from '../../../api/recalc/endpoints/jobSpecificationApiSlice';
import { useGetUnitsByAccountQuery } from '../../../api/recalc/endpoints/unitsApiSlice'
import { useUpdateSubscriptionMutation, useGetSubscriptionsByAccountQuery, usePostSubscriptionMutation } from '../../../api/recalc/endpoints/jobSubscriptionApiSlice';
import { useGetGatewaysByAccountQuery } from '../../../api/recalc/endpoints/gatewayApiSlice';
import { usePostSubscriptionOnBuildingMutation } from '../../../api/recalc/endpoints/buildingApiSlice';
import { useGetAccountsQuery } from '../../../api/recalc/endpoints/accountsApiSlice';
import { useGetBuildingsQuery, useGetBuildingsByAccountQuery } from '../../../api/recalc/endpoints/buildingApiSlice';
import { useLocation } from 'react-router-dom';
import Slide from '@mui/material/Slide';
import Fade from '@mui/material/Fade';
import { Typography, ListItem, ListItemText, List } from '@mui/material';

import { useGetSupportedAlarmJobTypesQuery, useGetSupportedPeoJobTypesQuery, usePostSubscribePeoAlarmsMutation } from '../../../api/subscription-manager/alarmSlice';

import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../redux/snackbarSlice';
import { getAllAccounts } from '../../../redux/allAccountsSlice';

const SubscriptionDataGrid = React.lazy(() =>
  import('remote/SubscriptionDataGrid').catch(() => ({
    default: () => <div>Failed to load</div>,
  }))
);

const JobSubscriptionCreate = React.lazy(() =>
  import('remote/JobSubscriptionCreate').catch(() => ({
    default: () => <div>Failed to load</div>,
  }))
);

const FullscreenDialog = React.lazy(() =>
  import('remote/FullscreenDialog').catch(() => ({
    default: () => <div>Failed to load</div>,
  }))
);

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function extractNumberFromURL(url) {
  const matches = url?.match(/(\d+)$/);
  
  if (matches) {
    return parseInt(matches[1], 10);
  } else {
    return null;
  }
}

const BuildingsDetailPage = () => {
  
  // Access the state passed from navigate
  const { subscriptionName } = useParams(); // Check if we're on a subscription page

  const selectedAccount = useSelector(getSelectedAccount);
  const selectedBuilding = useSelector(getSelectedBuilding);
  const selectedSubscription = useSelector(getSelectedSubscription);
  const allAccounts = useSelector(getAllAccounts);
  const dispatch = useDispatch();


  const [dialogOpen, setDialogOpen] = useState(false);
  const [defaultAlarmDialogOpen, setDefaultAlarmDialogOpen] = useState(false);
  const [createDefaultAlarmResponse, setCreateDefaultAlarmResponse] = useState(null);
  
  const [updateSubscription, { isSuccess: isSuccessUpdateSubscription, isError: isErrorUpdateingSubscription, error: errorUpdateSubscription }] = useUpdateSubscriptionMutation();
  const [postSubscription, { isSuccess: isSuccessPostSubscription, isLoading, isError: isErrorPostSubscription, error: errorPostSubscription }] = usePostSubscriptionMutation();
  const [postSubscriptionOnBuilding, { isSuccess: isSuccessPostSubscriptionOnBuilding, isLoading: isLoadingPostSubscriptionOnBuilding, isError: isErrorPostSubscriptionOnBuilding, error: errorPostSubscriptionOnBuilding }] = usePostSubscriptionOnBuildingMutation();
  
  const { data: jobSpecifications, isLoading: isLoadingJobSpecifications } = useGetJobSpecificationsQuery();
  const { data: units, isLoading: isLoadingUnits } = useGetUnitsByAccountQuery(selectedAccount?.AccountId, { skip: !selectedAccount?.AccountId });
  const { data: gateways, isLoading: isLoadingGateways } = useGetGatewaysByAccountQuery(selectedAccount?.AccountId, { skip: !selectedAccount?.AccountId });
  const { data: subscriptions } = useGetSubscriptionsByAccountQuery(selectedAccount?.AccountId, { skip: !selectedAccount?.AccountId });
  const { data: accounts } = useGetAccountsQuery();
  const { data: buildings } = useGetBuildingsQuery();
  const { data: buildingsByAccount } = useGetBuildingsByAccountQuery(selectedAccount?.AccountId, { skip: !selectedAccount?.AccountId });

  const {data: supportedAlarmJobTypes, isLoading: isLoadingSupportedAlarmJobTypes} = useGetSupportedAlarmJobTypesQuery()
  const {data: supportedPeoJobTypes, isLoading: isLoadingSupportedPeoJobTypes} = useGetSupportedPeoJobTypesQuery()
  const [postSubscribePeoAlarms,{data, isLoading: isLoadingPostSubscribePeoAlarms}] = usePostSubscribePeoAlarmsMutation()

  const [pause, { isLoading: isLoadingPause, isSuccess: isSuccessPause, isError: isErrorPause, error: errorPause }] = usePutPauseStateCodeMutation()
  const [resume, { isLoading: isLoadingResume, isSuccess: isSuccessResume, isError: isErrorResume, error: errorResume }] = usePutResumeStateCodeMutation()
  const [activate, { isLoading: isLoadingActivate, isSuccess: isSuccessActivate, isError: isErrorActivate, error: errorActivate }] = usePutActivateStateCodeMutation()
  const [resetInitialization, { isLoading: isLoadingResetInitialization, isSuccess: isSuccessResetInitialization, isError: isErrorResetInitialization, error: errorResetInitialization }] = usePutResetInitializationStateCodeMutation()
  const [resetHeatControl, {isLoading: isLoadingReset, isSuccess: isSuccessReset, isError: isErrorReset, error: errorReset}] = usePostResetPredictiveHeatControlMutation();

  const handleOpen = () => setDialogOpen(true);
  const handleClose = () => {
    setDialogOpen(false);
  };

  const subscriptionsOnBuilding = subscriptions && subscriptions
  .filter(subscription => subscription.BuildingId === selectedBuilding.BuildingId)
  .sort((a, b) => b.JobSubscriptionId - a.JobSubscriptionId);

  const eligibleForDefaultAlarms = subscriptionsOnBuilding && subscriptionsOnBuilding.find(sub => sub.JobType === "PredictiveHeatControl") ? true : false;
  
  const navigate = useNavigate();

  const handleSubscriptionRowClicked = (subscription) => {
    // Switch accounts if necessary
    if (selectedAccount.AccountId !== subscription.AccountId) {
      dispatch(setSelectedAccount(allAccounts.find(account => account.AccountId === subscription.AccountId)));
    }

    // Clean the subscription object to avoid circular references
    const { 
      id, 
      BuildingName, 
      DisplayName, 
      AccountId,
      JobType,
      State,
      GatewayHealthCheck,
      DataInput, 
      LatestJobRun,
      AlgorithmInput, 
      UserInput, 
      ...rest } = subscription;
    
    const cleanedSubscription = {
      BuildingName,
      DisplayName,
      AccountId,
      JobType,
      DataInput,
      State,
      GatewayHealthCheck,
      LatestJobRun,
      AlgorithmInput,
      UserInput,
      JobSubscriptions: selectedBuilding.JobSubscriptions,
      id,
      // Add any other required fields from the subscription object
      // Make sure to exclude complex properties like functions, components, or Emotion-related properties
    };
    
    dispatch(setSelectedSubscription(cleanedSubscription));
    const encodedSubscriptionName = encodeURIComponent(subscription.DisplayName);
    // Navigate to the building's subscription page
    navigate(`/buildings/${selectedBuilding.Name}/${encodedSubscriptionName}`, { replace: true });
  };

  const handleUpdateSubscription = async ({ subscriptionId, ...values }) => {
    try {
      const response = await updateSubscription({ id: subscriptionId, patch: values }).unwrap();
      dispatch(showSnackbar({ message: 'Subscription was updated', severity: 'success', response: response }));
    } catch (error) {
      dispatch(showSnackbar({ message: 'Error updating subscription', severity: 'error', response: error }));
    }
  };

  useEffect(() => {
    if (selectedAccount.AccountId !== selectedBuilding.AccountId) {
      navigate('/buildings')
    }
  }, [selectedAccount, selectedBuilding, navigate]);

  useEffect(() => {
    if (isSuccessPause) {
      dispatch(showSnackbar({ message: 'Subscription was paused', severity: 'success' }));
    } else if (isErrorPause) {
      dispatch(showSnackbar({ message: 'Error pausing subscription', severity: 'error', response: errorPause }));
    } else if (isSuccessResume) {
      dispatch(showSnackbar({ message: 'Subscription was resumed', severity: 'success' }));
    } else if (isErrorResume) {
      dispatch(showSnackbar({ message: 'Error resuming subscription', severity: 'error', response: errorResume }));
    } else if (isSuccessActivate) {
      dispatch(showSnackbar({ message: 'Subscription was activated', severity: 'success' }));
    } else if (isErrorActivate) {
      dispatch(showSnackbar({ message: 'Error activating subscription', severity: 'error', response: errorActivate }));
    }
  }, [isSuccessPause, isErrorPause, errorPause, isSuccessResume, isErrorResume, errorResume, isSuccessActivate, isErrorActivate, errorActivate]);


  const handleSubmit = async (data) => {
    try {
      const response = await postSubscription(data).unwrap();
      dispatch(showSnackbar({ message: 'Subscription was created', severity: 'success', response: response }));
      const subscriptionId = extractNumberFromURL(response.location);
      if (subscriptionId) {
        try {
        const secondResponse = await postSubscriptionOnBuilding({ buildingId: selectedBuilding.BuildingId, subscriptionId });
        dispatch(showSnackbar({ message: `Subscription was added to ${selectedBuilding.Name}`, severity: 'success', response: secondResponse }));
        } catch (error) {
          dispatch(showSnackbar({ message: 'Error adding subscription to building', severity: 'error', response: error }));
        }
      }
  
      setTimeout(() => {
        setDialogOpen(false);
      }, 5000);
      
    } catch (error) {
      dispatch(showSnackbar({ message: 'Error creating subscription', severity: 'error', response: error }));
    }
  }

  const handleCreateDefaultAlarms = async (data) => {
    try {
      const response = await postSubscribePeoAlarms(data).unwrap();
      if (response && response.errors.length > 0) {
        setCreateDefaultAlarmResponse(response);
        dispatch(showSnackbar({ message: 'Error when creating default alarms.', severity: 'error', response: response }));
      } else if (response.warnings.length > 0) {
        setCreateDefaultAlarmResponse(response);
        dispatch(showSnackbar({ message: 'Warnings when creating default alarms.', severity: 'warning', response: response }));
      } else {
        setCreateDefaultAlarmResponse(null);
        dispatch(showSnackbar({ message: 'Default alarms were created successfully!', severity: 'success', response: response }));
      }
      setDefaultAlarmDialogOpen(false);
    } catch (error) {
      dispatch(showSnackbar({ message: 'Error creating default alarms', severity: 'error', response: error }));
    }
  }

  return (
    <Container maxWidth='xxl'>
       {isLoadingJobSpecifications || isLoadingGateways || isLoadingUnits ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80vh' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Suspense fallback={
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80vh' }}>
            <CircularProgress />
          </Box>
        }>
          {!subscriptionName && (
          <Fade in={true} timeout={500}>
          <Box>
          <SubscriptionDataGrid 
            subscriptions={subscriptionsOnBuilding}
            handleSubscriptionRowClicked={handleSubscriptionRowClicked} 
            jobSpecifications={jobSpecifications}
            units={units}
            accounts={accounts}
            buildings={buildings}
            gateways={gateways}
            onSubmit={handleUpdateSubscription}
            handleOpen={handleOpen}
            isLoading={isLoadingJobSpecifications || isLoadingUnits || isLoadingGateways}
            pause={pause}
            resume={resume}
            activate={activate}
            resetInitialization={resetInitialization}
          />
          </Box>
          </Fade>
          )}
          <FullscreenDialog
            open={dialogOpen}
            onClose={handleClose}
            title="New Job Subscription"
            TransitionComponent={Transition}
          >
            <JobSubscriptionCreate 
              selectedAccount={selectedAccount} 
              location={selectedBuilding?.Location}
              building={selectedBuilding}
              submit={handleSubmit} 
              handleClose={handleClose}
              defaultAlarmDialogOpen={defaultAlarmDialogOpen}
              handleCreateDefaultAlarmsClick={() => setDefaultAlarmDialogOpen(true)}
              handleCreateDefaultAlarms={handleCreateDefaultAlarms}
              onCloseDefaultAlarmDialog={() => setDefaultAlarmDialogOpen(false)}
              createDefaultAlarmResponse={createDefaultAlarmResponse}
              units={units}
              gateways={gateways}
              jobSpecifications={jobSpecifications}
              subscriptionsOnBuilding={subscriptionsOnBuilding}
              eligibleForDefaultAlarms={eligibleForDefaultAlarms}
              supportedAlarmJobTypes={supportedAlarmJobTypes}
              supportedPeoJobTypes={supportedPeoJobTypes}
              isLoading={isLoadingPostSubscriptionOnBuilding}
            />
          </FullscreenDialog>
        </Suspense>
      ) }
      <Outlet />
    </Container>
  )
}

export default BuildingsDetailPage